// Agent parameter tables (PSD §3.2 AgentParams / TODO Task 0.4, 3.7)
import { Agent, type AgentParams } from '@/types';

/**
 * Population PK defaults for aminoglycosides.
 * Values from PSD §3.2 + standard clinical literature.
 * halfLifeHr derived as ln(2)/kelEstimatePerHr.
 */
const LN2 = Math.log(2);

function halfLife(kel: number): number {
  return kel > 0 ? LN2 / kel : 0;
}

const GENT_TOBRA_KEL = 0.0024 * 60 + 0.01; // CrCl ~60 placeholder Kel; overridden by renal module
const AMIKACIN_KEL = GENT_TOBRA_KEL;

const gentamicinParams: AgentParams = {
  agent: Agent.Gentamicin,
  eiDoseMgKg: 7,
  eiDoseMinMgKg: 5,
  eiDoseMaxMgKg: 7,
  traditionalDoseMgKg: 1.4,
  targetPeakMg: 8,
  targetTroughMg: 1.5,
  targetPeakRange: [6, 10],
  targetTroughRange: [1, 2],
  vdLkg: 0.26,
  kelEstimatePerHr: GENT_TOBRA_KEL,
  roundingIncrementMg: 20,
  halfLifeHr: halfLife(GENT_TOBRA_KEL),
  synergyPeakTarget: 3.5,
  synergyTroughTarget: 0.5,
};

const tobramycinParams: AgentParams = {
  ...gentamicinParams,
  agent: Agent.Tobramycin,
};

const amikacinParams: AgentParams = {
  agent: Agent.Amikacin,
  eiDoseMgKg: 15,
  eiDoseMinMgKg: 15,
  eiDoseMaxMgKg: 15,
  traditionalDoseMgKg: 7.5,
  targetPeakMg: 30,
  targetTroughMg: 5,
  targetPeakRange: [25, 35],
  targetTroughRange: [4, 8],
  vdLkg: 0.27,
  kelEstimatePerHr: AMIKACIN_KEL,
  roundingIncrementMg: 100,
  halfLifeHr: halfLife(AMIKACIN_KEL),
  synergyPeakTarget: 10,
  synergyTroughTarget: 2,
};

// Phase 3 — plazomicin only (Q1 resolved).
const plazomicinParams: AgentParams = {
  agent: Agent.Plazomicin,
  eiDoseMgKg: 7.5,
  eiDoseMinMgKg: 7.5,
  eiDoseMaxMgKg: 7.5,
  traditionalDoseMgKg: 7.5,
  targetPeakMg: 25,
  targetTroughMg: 1,
  targetPeakRange: [20, 30],
  targetTroughRange: [0.5, 2],
  vdLkg: 0.27,
  kelEstimatePerHr: GENT_TOBRA_KEL,
  roundingIncrementMg: 50,
  halfLifeHr: halfLife(GENT_TOBRA_KEL),
  synergyPeakTarget: 0,
  synergyTroughTarget: 0,
};

// TODO: add streptomycin/neomycin if requested by clinical users
const streptomycinParams: AgentParams = {
  agent: Agent.Streptomycin,
  eiDoseMgKg: 0,
  eiDoseMinMgKg: 0,
  eiDoseMaxMgKg: 0,
  traditionalDoseMgKg: 15, // mg/kg (15 mg/kg IM typical)
  targetPeakMg: 0,
  targetTroughMg: 0,
  targetPeakRange: [0, 0],
  targetTroughRange: [0, 0],
  vdLkg: 0.26,
  kelEstimatePerHr: GENT_TOBRA_KEL,
  roundingIncrementMg: 100,
  halfLifeHr: halfLife(GENT_TOBRA_KEL),
  synergyPeakTarget: 0,
  synergyTroughTarget: 0,
};

const neomycinParams: AgentParams = {
  agent: Agent.Neomycin,
  eiDoseMgKg: 0,
  eiDoseMinMgKg: 0,
  eiDoseMaxMgKg: 0,
  traditionalDoseMgKg: 7.5,
  targetPeakMg: 0,
  targetTroughMg: 0,
  targetPeakRange: [0, 0],
  targetTroughRange: [0, 0],
  vdLkg: 0.26,
  kelEstimatePerHr: GENT_TOBRA_KEL,
  roundingIncrementMg: 50,
  halfLifeHr: halfLife(GENT_TOBRA_KEL),
  synergyPeakTarget: 0,
  synergyTroughTarget: 0,
};

export const AGENT_PARAMS: Record<Agent, AgentParams> = {
  [Agent.Gentamicin]: gentamicinParams,
  [Agent.Tobramycin]: tobramycinParams,
  [Agent.Amikacin]: amikacinParams,
  [Agent.Plazomicin]: plazomicinParams,
  [Agent.Streptomycin]: streptomycinParams,
  [Agent.Neomycin]: neomycinParams,
};

export function getAgentParams(agent: Agent): AgentParams {
  return AGENT_PARAMS[agent];
}
